import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, FileText, Settings, LogOut, 
  Menu, X, Activity, Shield, History, ClipboardCheck,
  CheckCircle2, Clock, AlertTriangle, ArrowRight, Loader2,
  MessageSquare, Eye, Download, Info, Printer, CalendarClock,
  FileEdit, Search, PenTool, Check, BookOpen, KeyRound, FileCheck2
} from 'lucide-react';
import ProfilePage from './Profile';
import UserList from './UserList';
import AuditTrail from './AuditTrail';
import BatchUpload from './BatchUpload';
import MasterDokumen from './MasterDokumen';
import UploadBerkas from './UploadBerkas';
import DocumentManager from './DocumentManager';
import Inputpak from './InputPAK';
import VerifikasiPAK from './VerifikasiPAK';
import HelpdeskUser from './HelpdeskUser';
import HelpdeskAdmin from './HelpdeskAdmin';
import TteVerificationPage from '../pages/TteVerificationPage';
import KpEntryPage from '../pages/kp/KpEntryPage';
import RekapUsulanPage from '../pages/kp/RekapUsulanPage';
import KpSatlakReviewPage from '../pages/kp/KpSatlakReviewPage'; // SATLAK_VERIF_KP_V1
import AccountCenter from './AccountCenter';
import TteArchive from './TteArchive';
import KpPilotAdmin from './KpPilotAdmin';
import MaintenanceAdmin from './MaintenanceAdmin';

import html2pdf from 'html2pdf.js';
import TabelPreviewPAK from './TabelPreviewPAK';

const GOLONGAN_MAP = {
  'III/a': { koef: 12.5, akLama: 50, jenjang: 100 },
  'III/b': { koef: 12.5, akLama: 50, jenjang: 100 },
  'III/c': { koef: 25, akLama: 100, jenjang: 200 },
  'III/d': { koef: 25, akLama: 100, jenjang: 200 },
  'IV/a': { koef: 37.5, akLama: 150, jenjang: 450 },
  'IV/b': { koef: 37.5, akLama: 150, jenjang: 450 },
  'IV/c': { koef: 37.5, akLama: 150, jenjang: 450 },
  'IV/d': { koef: 50, akLama: 200, jenjang: -1 }, 
  'IV/e': { koef: 50, akLama: 0, jenjang: -1 }
};

const monthNames = ["", "Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

const safeParseDate = (inputDate) => {
    if (inputDate === undefined || inputDate === null || inputDate === '') return null;
    if (inputDate instanceof Date) {
        if (isNaN(inputDate.getTime())) return null;
        return { year: inputDate.getFullYear(), month: inputDate.getMonth() + 1, day: inputDate.getDate() };
    }
    const dateStr = String(inputDate).trim();
    if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length >= 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10); 
            const year = parseInt(parts[2], 10);
            if (!isNaN(day) && !isNaN(month) && !isNaN(year)) return { year, month, day };
        }
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
    if (dateStr.includes('-')) {
        const parts = dateStr.split('T')[0].split('-');
        if (parts.length >= 3) {
            let year = parseInt(parts[0], 10);
            let month = parseInt(parts[1], 10); 
            let day = parseInt(parts[2], 10);
            if (year < 1000) { 
                day = parseInt(parts[0], 10);
                year = parseInt(parts[2], 10); 
            }
            if (!isNaN(day) && !isNaN(month) && !isNaN(year)) return { year, month, day };
        }
    }
    return null;
};

// --- KOMPONEN UTAMA DASHBOARD ---
const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState({ name: '', role: '' });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Only the backend determines teacher eligibility. Hidden while checking.
  const [kpAllowed, setKpAllowed] = useState(false);

  useEffect(() => {
    if (user.role !== 'guru') {
      setKpAllowed(false);
      return undefined;
    }
    let mounted = true;
    let controller = null;
    const check = async () => {
      if (controller) controller.abort();
      controller = new AbortController();
      const signal = controller.signal;
      const token = localStorage.getItem('token');
      if (!token) { if (mounted) setKpAllowed(false); return; }
      try {
        const api = process.env.REACT_APP_API_URL || 'https://api-pak.sudindikjt2.site';
        const response = await fetch(`${api}/api/kp/access`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
          cache: 'no-store', signal
        });
        const result = response.ok ? await response.json() : null;
        if (mounted && !signal.aborted) setKpAllowed(Boolean(response.ok && result?.allowed === true));
      } catch (_) {
        if (mounted && !signal.aborted) setKpAllowed(false);
      }
    };
    setKpAllowed(false);
    check();
    const interval = setInterval(check, 60000);
    window.addEventListener('focus', check);
    return () => {
      mounted = false;
      if (controller) controller.abort();
      clearInterval(interval);
      window.removeEventListener('focus', check);
    };
  }, [user.role]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userName = localStorage.getItem('userName');
    const userRole = localStorage.getItem('userRole');

    if (!token) {
      navigate('/');
    } else {
      setUser({ name: userName || 'User', role: userRole || 'guru' });
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const renderContent = () => {
    switch(activeTab) {
      case 'dashboard': return <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;
      case 'account-security': return <AccountCenter role={user.role} />;
      case 'kp-pilot-admin': return user.role === 'admin' ? <KpPilotAdmin/> : <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;
      case 'maintenance': return user.role === 'admin' ? <MaintenanceAdmin /> : <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;
      case 'tte-asli-saya': return <TteArchive role={user.role} onAccount={() => setActiveTab('account-security')} />;
      
      // Menu Khusus Guru
      case 'profil': return (user.role === 'guru') ? <ProfilePage onSwitchTab={setActiveTab} /> : <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;
      case 'uploadberkas': return (user.role === 'guru') ? <UploadBerkas onSwitchTab={setActiveTab} /> : <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;
      case 'inputpak': return (user.role === 'guru') ? <Inputpak onSwitchTab={setActiveTab} /> : <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;
      
      // Menu Verifikasi PAK: Admin ATAU Satlak
      case 'verifikasi-pak': 
        return (user.role === 'admin' || user.role === 'satlak') 
          ? <VerifikasiPAK /> 
          : <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;
          
      // Verifikasi TTE: Admin ATAU Satlak
      case 'verifikasi-tte':
        return (user.role === 'admin' || user.role === 'satlak')
          ? <TteVerificationPage />
          : <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;

      // Usulan Kenaikan Pangkat:
      // sementara hanya Admin selama masa uji coba
      case 'kenaikan-pangkat':
        return user.role === 'admin'
          ? <KpEntryPage />
          : <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;

      // SATLAK_VERIF_KP_V1: ruang kerja Satlak, tidak membuka simulasi Admin.
      case 'satlak-verifikasi-kp':
        return user.role === 'satlak'
          ? <KpSatlakReviewPage />
          : <DashboardHome user={user} onSwitchTab={setActiveTab} />;

      // Rekap Usulan KP: Admin & Satlak
      case 'rekap-usulan-kp':
        return (user.role === 'admin' || user.role === 'satlak')
          ? <RekapUsulanPage />
          : <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;

      // Menu Khusus Admin Saja
      case 'users': return user.role === 'admin' ? <UserList /> : <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;
      case 'logs': return user.role === 'admin' ? <AuditTrail /> : <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;
      case 'batch-user': return user.role === 'admin' ? <BatchUpload /> : <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;
      case 'doc-manager': return user.role === 'admin' ? <DocumentManager /> : <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;
      case 'master-dokumen': return user.role === 'admin' ? <MasterDokumen /> : <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />; 
      
      case 'helpdesk':
        if (user.role === 'admin') return <HelpdeskAdmin />;
        if (user.role === 'guru' || user.role === 'satlak') return <HelpdeskUser />;
        return <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;
        
      default: return <DashboardHome user={user} onSwitchTab={setActiveTab} kpAllowed={kpAllowed} />;
    }
  };

  return (
    <div className="flex h-screen bg-[#F8FAFC] font-sans text-slate-800 overflow-hidden">
      
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      <aside className={`
        fixed md:static 
        inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white flex flex-col shadow-2xl transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="h-16 md:h-20 flex items-center justify-between px-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img src="/logo ptk.png" alt="Logo PTK" className="w-10 h-10 object-contain drop-shadow-md" />
            <div>
              <h1 className="font-serif text-lg tracking-wide leading-none">SIPAK</h1>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest">SudinDik JT2</p>
            </div>
          </div>

          <button onClick={() => setIsSidebarOpen(false)} className="md:hidden text-slate-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

       <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <p className="text-xs font-bold text-slate-500 uppercase mb-2 px-2 tracking-wider">Menu Utama</p>
          
          <NavButton icon={<LayoutDashboard size={20}/>} label="Dashboard" active={activeTab === 'dashboard'} onClick={() => { setActiveTab('dashboard'); setIsSidebarOpen(false); }} />
          {(user.role !== 'guru' || kpAllowed) && (
            <NavButton icon={<BookOpen size={20}/>} label="Panduan Usulan KP" active={false} onClick={() => navigate('/panduan-kenaikan-pangkat')} />
          )}
          <NavButton icon={<KeyRound size={20}/>} label="Keamanan Akun" active={activeTab === 'account-security'} onClick={() => {setActiveTab('account-security');setIsSidebarOpen(false);}} />
          <NavButton icon={<FileCheck2 size={20}/>} label={'Dokumen PAK TTE'} active={activeTab === 'tte-asli-saya'} onClick={() => {setActiveTab('tte-asli-saya');setIsSidebarOpen(false);}} />
          
          {user.role === 'guru' && (
            <>
              <NavButton icon={<Users size={20}/>} label="Profil Pegawai" active={activeTab === 'profil'} onClick={() => { setActiveTab('profil'); setIsSidebarOpen(false); }} />
              <NavButton icon={<FileText size={20}/>} label="Upload Berkas" active={activeTab === 'uploadberkas'} onClick={() => { setActiveTab('uploadberkas'); setIsSidebarOpen(false); }} />
              <NavButton icon={<Activity size={20}/>} label="Input PAK" active={activeTab === 'inputpak'} onClick={() => { setActiveTab('inputpak'); setIsSidebarOpen(false); }} />
            </>
          )}

          {(user.role === 'admin' || user.role === 'satlak') && (
            <>
              <p className="text-xs font-bold text-slate-500 uppercase mt-6 mb-2 px-2 tracking-wider">Verifikasi</p>
              <NavButton icon={<ClipboardCheck size={20}/>} label="Verifikasi PAK" active={activeTab === 'verifikasi-pak'} onClick={() => { setActiveTab('verifikasi-pak'); setIsSidebarOpen(false); }} />
              <NavButton
                icon={<PenTool size={20}/>}
                label="Verifikasi TTE"
                active={activeTab === 'verifikasi-tte'}
                onClick={() => {
                  setActiveTab('verifikasi-tte');
                  setIsSidebarOpen(false);
                }}
              />

              <NavButton
                icon={<ClipboardCheck size={20}/>}
                label="Rekap Usulan"
                active={activeTab === 'rekap-usulan-kp'}
                onClick={() => {
                  setActiveTab('rekap-usulan-kp');
                  setIsSidebarOpen(false);
                }}
              />

              {/* SATLAK_VERIF_KP_V1: menu verifikasi terbatas pada kecamatan akun Satlak. */}
              {user.role === 'satlak' && (
                <NavButton
                  icon={<FileEdit size={20}/>}
                  label="Usulan Kenaikan Pangkat"
                  active={activeTab === 'satlak-verifikasi-kp'}
                  onClick={() => {
                    setActiveTab('satlak-verifikasi-kp');
                    setIsSidebarOpen(false);
                  }}
                />
              )}

              {user.role === 'admin' && (
                <NavButton
                  icon={<FileEdit size={20}/>}
                  label="Usulan Kenaikan Pangkat"
                  active={activeTab === 'kenaikan-pangkat'}
                  onClick={() => {
                    setActiveTab('kenaikan-pangkat');
                    setIsSidebarOpen(false);
                  }}
                />
              )}
            </>
          )}

          {user.role === 'admin' && (
            <>
              <p className="text-xs font-bold text-slate-500 uppercase mt-6 mb-2 px-2 tracking-wider">Manajemen Sistem</p>
              <NavButton icon={<Shield size={20}/>} label="Data Pengguna" active={activeTab === 'users'} onClick={() => { setActiveTab('users'); setIsSidebarOpen(false); }} />
              <NavButton icon={<History size={20}/>} label="Audit Trail" active={activeTab === 'logs'} onClick={() => { setActiveTab('logs'); setIsSidebarOpen(false); }} />
              <NavButton icon={<Settings size={20}/>} label="Akses KP Terbatas" active={activeTab === 'kp-pilot-admin'} onClick={() => { setActiveTab('kp-pilot-admin'); setIsSidebarOpen(false); }} />
              <NavButton icon={<Settings size={20}/>} label="Pemeliharaan Sistem" active={activeTab === 'maintenance'} onClick={() => { setActiveTab('maintenance'); setIsSidebarOpen(false); }} />
              <NavButton icon={<Users size={20}/>} label="Upload User Batch" active={activeTab === 'batch-user'} onClick={() => { setActiveTab('batch-user'); setIsSidebarOpen(false); }} />
              <NavButton icon={<Settings size={20}/>} label="Pengaturan" active={activeTab === 'doc-manager'} onClick={() => { setActiveTab('doc-manager'); setIsSidebarOpen(false); }} />
            </>
          )}

          <p className="text-xs font-bold text-slate-500 uppercase mt-6 mb-2 px-2 tracking-wider">Pusat Bantuan</p>
          <NavButton 
            icon={<MessageSquare size={20}/>} 
            label={user.role === 'admin' ? "Monitoring Helpdesk" : "Lapor Kendala (Helpdesk)"} 
            active={activeTab === 'helpdesk'} 
            onClick={() => { setActiveTab('helpdesk'); setIsSidebarOpen(false); }} 
          />
        </nav>

        <div className="p-4 border-t border-white/10">
          <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg w-full transition-colors">
            <LogOut size={18} />
            <span className="text-sm font-medium">Keluar</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col h-full overflow-hidden w-full relative">
        <header className="h-16 md:h-20 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-8 shrink-0">
          <button onClick={() => setIsSidebarOpen(true)} className="md:hidden text-slate-600 p-2 -ml-2 hover:bg-slate-100 rounded-lg">
            <Menu size={24} />
          </button>
          <h2 className="hidden md:block font-serif text-xl text-slate-900">Sistem Informasi PAK</h2>
          
          <div className="flex items-center gap-4 ml-auto">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-bold text-slate-900 truncate max-w-[150px]">{user.name}</p>
              <p className="text-xs text-slate-500 capitalize">{user.role}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center font-bold text-slate-600 text-sm">
              {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto bg-[#F8FAFC]">
           {renderContent()}
        </div>
      </main>
    </div>
  );
};

const NavButton = ({ icon, label, active, onClick }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
      active ? 'bg-amber-500 text-slate-900 shadow-lg shadow-amber-900/20' : 'text-slate-400 hover:bg-white/5 hover:text-white'
    }`}
  >
    {icon}
    {label}
  </button>
);

// --- KOMPONEN DashboardHome ---
const DashboardHome = ({ user, onSwitchTab, kpAllowed = false }) => {
  const [pakData, setPakData] = useState({ loading: true, status: null });
  const [recentList, setRecentList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [deadlineInfo, setDeadlineInfo] = useState({ date: null, isClosed: false });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewCalculation, setPreviewCalculation] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const printRef = useRef(null);
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 11) return 'Selamat Pagi';
    if (hour < 15) return 'Selamat Siang';
    if (hour < 18) return 'Selamat Sore';
    return 'Selamat Malam';
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      if (user.role === 'satlak' || user.role === 'admin') {
        try {
          const res = await fetch(`${BASE_URL}/api/admin/pak`, { headers });
          if (res.ok) setRecentList(await res.json());
        } catch (err) { console.error("Gagal load data", err); } 
        finally { setLoadingList(false); }
        
        if (user.role === 'satlak') {
           try {
             const resDate = await fetch(`${BASE_URL}/api/settings/deadline`);
             if (resDate.ok) {
               const { deadline } = await resDate.json();
               if (deadline) {
                 const deadlineDate = new Date(deadline);
                 deadlineDate.setHours(23, 59, 59, 999);
                 setDeadlineInfo({ date: deadline, isClosed: new Date() > deadlineDate });
               }
             }
           } catch (err) {}
        }
        return;
      }

      // FETCH UNTUK ROLE GURU
      try {
        const [resStatus, resProfile] = await Promise.all([
            fetch(`${BASE_URL}/api/pak/status`, { headers }),
            fetch(`${BASE_URL}/api/profile`, { headers })
        ]);
        
        if (resStatus.ok) {
          const data = await resStatus.json();
          setPakData({ loading: false, status: data });
        } else {
          setPakData({ loading: false, status: null });
        }

        if (resProfile.ok) {
            setProfileData(await resProfile.json());
        }
      } catch (err) {
        console.error("Gagal load status dashboard", err);
        setPakData({ loading: false, status: null });
      }
    };

    fetchDashboardData();
  }, [user.role, BASE_URL]);

  // ==============================================================
  // KALKULATOR DASHBOARD (Disinkronkan dengan VerifikasiPAK.jsx)
  // ==============================================================
const calculateData = (item) => {
    if (item.is_empty) return null;
    const rawAkLama = String(item.ak_lama || '0').replace(',', '.');
    let akLama = Number(rawAkLama) || 0;

    const golInfo = GOLONGAN_MAP[item.golongan] || GOLONGAN_MAP['III/a'];
    const golonganPegawai = String(item.golongan || '').toLowerCase().replace(/\s/g, '');
    
    const isGol3A = golonganPegawai === 'iii/a' || golonganPegawai === 'iiia';
    const isBaseGol = ['iii/a', 'iii/c', 'iv/a', 'iv/d'].includes(golonganPegawai);
    let akPG = item.is_pencantuman_gelar === 'Ya' ? golInfo.akLama * 0.25 : 0;
    item.ak_pg = akPG;
    
    let skpData = typeof item.skp_data === 'string' ? JSON.parse(item.skp_data) : (item.skp_data || []);
    let validSkpList = skpData.filter(s => s && s.kode && s.kode !== '');

  // --- UBAH MENJADI ---
const isKonversi = (item.jenis_pak || 'Integrasi').toLowerCase() === 'konversi';
const labelJenisPak = isKonversi 
    ? (isGol3A ? 'Angka Kredit Dasar' : 'Angka Kredit Sebelumnya') 
    : 'Angka Kredit Integrasi';
    let tahunBaseAK = '-';
    let periodeCetak = '-';
    const parsedMulai = safeParseDate(item.masa_penilaian_mulai);
    const parsedSelesai = safeParseDate(item.masa_penilaian_selesai);
    const parsedTmt = safeParseDate(item.tmt_jabatan_baru);
    const parsedTmtAsli = safeParseDate(item.tmt_jabatan);
    const isJabatanTidakSesuai = item.is_jabatan_sesuai === 'Tidak';

    // --- HELPER PERBANDINGAN TANGGAL (Format: YYYYMMDD) ---
    const getComparableDateNum = (pDate) => {
        if (!pDate) return 0;
        return pDate.year * 10000 + pDate.month * 100 + pDate.day;
    };

    const selesaiNum = getComparableDateNum(parsedSelesai);
    const tmtAsliNum = getComparableDateNum(parsedTmtAsli);

    // --- 1. DETEKSI RESET AK AWAL (Sesuai Logika Mas Pepin) ---
    const isGolKhususNol = ['iii/c', 'iv/a', 'iv/d'].includes(golonganPegawai);
    let isResetNol = false;

    if (isKonversi && isGolKhususNol) {
        if (parsedSelesai && parsedTmtAsli) {
            // Jika masa penilaian PAK sebelumnya SEBELUM TMT Jabfung -> Reset 0
            if (selesaiNum < tmtAsliNum) {
                isResetNol = true;
            } else {
                // Jika masa penilaian PAK sebelumnya SETELAH/SAMA DENGAN TMT Jabfung -> Lanjut
                isResetNol = false; 
            }
        } else if (item.is_baru_naik_jenjang === 'Ya') {
            isResetNol = true; // Fallback jika user centang manual
        } else if (!parsedSelesai && parsedTmtAsli) {
            isResetNol = true; // Belum pernah punya PAK (selesai kosong), otomatis reset
        }
    }

    // Eksekusi reset AK
    if (isResetNol) {
        akLama = 0;
    }

    let startMonthNum, startYear, startDay = 1;
    let endYear = 2025, endMonthNum = 12, endDay = 31;

    if (isKonversi) {
        // --- 2. TENTUKAN KAPAN PERIODE SKP DIMULAI ---
        let shouldStartFromTmt = false;
        let forceEnd2025 = false; 
        
        if (isGol3A) {
            forceEnd2025 = true; // 3A dipaksa sampai akhir 2025
        } 
        
        // Sesuai Logika Mas Pepin: Jika PAK terakhir SEBELUM TMT Jabfung (atau kosong) -> Mulai dari TMT
        if (parsedTmtAsli) {
            if (!parsedSelesai || selesaiNum < tmtAsliNum) {
                shouldStartFromTmt = true;
            }
        }

        // --- 3. SETTING TANGGAL MULAI ---
       if (shouldStartFromTmt && parsedTmtAsli) {
            // Gunakan tanggal eksak TMT untuk semua golongan
            startDay = parsedTmtAsli.day; 
            startMonthNum = parsedTmtAsli.month;
            startYear = parsedTmtAsli.year;
            tahunBaseAK = startYear;
            validSkpList = validSkpList.filter(s => parseInt(s.tahun) >= startYear);
        } else if (parsedSelesai) {
            // Jika SETELAH TMT Jabfung -> Mulai dari H+1 (Masa Akhir Penilaian + 1)
            startDay = 1;
            startMonthNum = parsedSelesai.month + 1;
            startYear = parsedSelesai.year;
            if (startMonthNum > 12) { 
                startMonthNum = 1;
                startYear += 1; 
            }
            tahunBaseAK = parsedSelesai.year;
            validSkpList = validSkpList.filter(s => parseInt(s.tahun) >= startYear);
        } else {
            // Default Fallback
            startDay = 1;
            startMonthNum = 1;
            startYear = 2023;
            tahunBaseAK = startYear;
        }
// --- 4. SETTING TANGGAL SELESAI ---
    if (forceEnd2025) {
        endYear = 2025;
        endMonthNum = 12;
        endDay = 31;
    } else if (isJabatanTidakSesuai && parsedTmt) {
        if (parsedTmt.month === 12 && parsedTmt.day === 31) {
            endYear = parsedTmt.year;
            endMonthNum = 12;
            endDay = 31;
        } else {
            endYear = parsedTmt.year;
            endMonthNum = parsedTmt.month - 1; 
            if (endMonthNum <= 0) { 
                endMonthNum = 12;
                endYear -= 1; 
            }
            endDay = new Date(endYear, endMonthNum, 0).getDate();
        }
    } else {
        endYear = 2025;
        endMonthNum = 12;
        endDay = 31;
    }

    // [BARU] CEK BULAN DARI DATA SKP 2026
    const skp2026 = validSkpList.find(s => parseInt(s.tahun) === 2026);
    if (item.is_skp_2026 === 'Ya' || skp2026) {
        endYear = 2026;
        endMonthNum = (skp2026 && skp2026.bulan) ? parseInt(skp2026.bulan, 10) : 12; // Ambil bulan dari JSON
        endDay = new Date(2026, endMonthNum, 0).getDate();
    }
    } else {
        // ==========================================
        // LOGIKA INTEGRASI (TIDAK ADA RESET AK)
        // ==========================================
        if (parsedSelesai) {
            tahunBaseAK = parsedSelesai.year;
        } else {
            tahunBaseAK = 2025;
        }
        
        const is4896 = Math.abs(akLama - 48.96) < 0.01;
        if (isGol3A && parsedTmtAsli) {
            startDay = parsedTmtAsli.day;
            startMonthNum = parsedTmtAsli.month;
            startYear = parsedTmtAsli.year;
        } else if (is4896 && parsedTmtAsli) {
            startDay = parsedTmtAsli.day;
            startMonthNum = parsedTmtAsli.month;
            startYear = parsedTmtAsli.year;
        } else {
            startDay = 1;
            startMonthNum = 1;
            startYear = 2023; 
        }

        endDay = 31;
        endMonthNum = 12;
        endYear = 2025; 
        
        validSkpList = validSkpList.filter(s => parseInt(s.tahun) >= startYear);
    }

    // Susun String Periode Cetak
    const tglMulaiStr = startYear ? `${String(startDay).padStart(2,'0')}-${String(startMonthNum).padStart(2,'0')}-${startYear}` : '-';
    const tglSelesaiStr = `${String(endDay).padStart(2,'0')}-${String(endMonthNum).padStart(2,'0')}-${endYear}`;
    periodeCetak = `${tglMulaiStr} s.d ${tglSelesaiStr}`;

  let totalAKSKP = 0;
    const detailSKP = validSkpList.map(s => {
        const skpYear = parseInt(s.tahun);
        let bulan = 0;
        let mStart = 1; 
        let mEnd = 12;

        // --- TAMBAHAN KODE: Geser awal perhitungan tabel jika TMT BUKAN tanggal 1 ---
        let hitungMulaiBulan = startMonthNum;
        
        // Jika TMT lebih dari tanggal 1 (misal tgl 2, 25, dst), hitungan SKP mulai bulan depannya
        if (startDay > 1) {
            hitungMulaiBulan += 1;
        }
        // -------------------------------------------------------------------------

        if (skpYear < startYear || skpYear > endYear) {
            bulan = 0; mStart = 0; mEnd = 0;
        } else if (startYear === endYear && skpYear === startYear) {
            bulan = endMonthNum - hitungMulaiBulan + 1;  // Ubah startMonthNum jadi hitungMulaiBulan
            mStart = hitungMulaiBulan; mEnd = endMonthNum; // Ubah startMonthNum jadi hitungMulaiBulan
        } else if (skpYear === startYear) {
            bulan = 12 - hitungMulaiBulan + 1;             // Ubah startMonthNum jadi hitungMulaiBulan
            mStart = hitungMulaiBulan; mEnd = 12;          // Ubah startMonthNum jadi hitungMulaiBulan
        } else if (skpYear === endYear) {
            bulan = endMonthNum;
            mStart = 1; mEnd = endMonthNum;
        } else {
            bulan = 12;
            mStart = 1; mEnd = 12;
        }

        // Handle edge case jika perhitungan bulan menjadi minus/nol (misal TMT di akhir Desember)
        if (bulan <= 0) {
            bulan = 0;
            mStart = 0;
            mEnd = 0;
        }
        
        let predikat = '-'; let multiplier = 0;
        if (String(s.kode) === '1') { predikat = 'Amat Baik'; multiplier = 1.50; }
        else if (String(s.kode) === '2') { predikat = 'Baik'; multiplier = 1.00; }
        else if (String(s.kode) === '3') { predikat = 'Cukup'; multiplier = 0.75; }

        const akDidapat = (bulan / 12) * multiplier * golInfo.koef;
        totalAKSKP += akDidapat;

        let periodeStr = bulan > 0 ? `${monthNames[mStart]} - ${monthNames[mEnd]}` : '-';
        return { 
            ...s, 
            predikat, 
            bulan, 
            multiplier, 
            koef: golInfo.koef || 0, 
            periode: periodeStr,
            result: akDidapat,
            prosentaseStr: bulan === 12 ? `${multiplier * 100}%` : `(${bulan}/12) x ${multiplier * 100}%`,
            sudah_pak: s.sudah_pak 
        };
    }).filter(s => s.bulan > 0);

    const sisaAKLama = akLama; 
    const jumlahAkDiperoleh = sisaAKLama + totalAKSKP + akPG; 
    const akKonversiLama = isBaseGol ? 0 : golInfo.akLama;
    const totalKumulatif = jumlahAkDiperoleh; 
    const akKonversiBaru = totalKumulatif - akPG - akKonversiLama;
    const totalKonversi = akKonversiLama + akKonversiBaru; 
    
    const sisaPangkat = (akKonversiBaru + akPG) - golInfo.akLama; 
    const sisaJenjang = totalKumulatif - golInfo.jenjang;
    let autoStatusAk = 'Cukup';
    const isNaikJenjang = ['iii/b', 'iii/d', 'iv/c'].includes(golonganPegawai);
    
    if (sisaPangkat < 0) {
        autoStatusAk = 'Tidak Cukup';
    }
    if (isNaikJenjang && golInfo.jenjang !== -1 && sisaJenjang < 0) {
        autoStatusAk = 'Tidak Cukup';
    }

    return { 
      detailSKP, totalAKSKP, golInfo, sisaAKLama, jumlahAkDiperoleh, 
      akKonversiLama, akKonversiBaru, akPG, totalKonversi, totalKumulatif, sisaPangkat, sisaJenjang, 
      labelJenisPak, tahunBaseAK, periodeCetak, isKonversi,
      statusAk: item.status_ak || autoStatusAk,
      calcAutoStatusAk: autoStatusAk
    };
  };

  const handleOpenPreview = () => {
      if (!profileData || !pakData.status) return;
      const combinedData = {
          ...profileData,
          ak_lama: pakData.status.ak_lama,
          skp_data: pakData.status.skp_data,
          ak_pg: pakData.status.ak_pg,
          status_ak: pakData.status.status_ak,
          nomor_surat: pakData.status.nomor_surat
      };
      
      const calcData = calculateData(combinedData);
      if (calcData) {
          setPreviewCalculation(calcData);
          setShowPreviewModal(true);
      } else {
          alert('Data belum lengkap untuk dicetak.');
      }
  };

  const handleDownloadPDF = () => {
    const element = printRef.current;
    const opt = {
      margin:       0,
      filename:     `PAK_${profileData?.nip || 'Disetujui'}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true, logging: false },
      jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(element).save();
  };

  const StatusBadge = ({ status }) => {
    switch (status) {
      case 'Pending':
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200"><Clock size={10}/> PENDING</span>;
      case 'Disetujui':
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200"><CheckCircle2 size={10}/> DISETUJUI</span>;
      case 'Ditolak':
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700 border border-rose-200"><AlertTriangle size={10}/> DITOLAK</span>;
      default:
        return <span className="inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700">{status}</span>;
    }
  };

  const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const d = new Date(dateString);
      return `${d.getDate()}/${d.getMonth()+1}/${d.getFullYear()}, ${String(d.getHours()).padStart(2,'0')}.${String(d.getMinutes()).padStart(2,'0')}.${String(d.getSeconds()).padStart(2,'0')}`;
  };

  const renderGuruTimeline = () => {
      if (pakData.loading) {
          return (
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-center text-slate-500 gap-3">
                <Loader2 className="animate-spin" size={24} /> Memuat riwayat...
              </div>
          );
      }

      const s = (pakData.status?.status || '').toLowerCase();
      const isSubmitted = pakData.status?.submitted;
      const hasNomorSurat = pakData.status?.nomor_surat && pakData.status?.nomor_surat !== '-';
      const rejectNote = pakData.status?.catatan_penolakan;
      const updatedAt = pakData.status?.updated_at;
      
      const steps = [
          {
              id: 1,
              title: "Pengisian Data & Berkas",
              desc: "Melengkapi biodata dan mengunggah dokumen persyaratan secara mandiri.",
              icon: FileEdit,
              state: !isSubmitted ? 'active' : 'done'
          },
          {
              id: 2,
              title: "Verifikasi Satlak Kecamatan",
              desc: s === 'ditolak' ? `Revisi: "${rejectNote}"` : "Pemeriksaan keabsahan dokumen oleh tim verifikator kecamatan.",
              icon: Search,
              state: !isSubmitted ? 'wait' : (s === 'ditolak' ? 'error' : (s === 'pending' ? 'active' : 'done'))
          },
          {
              id: 3,
              title: "Antrean Cetak Suku Dinas",
              desc: "Pembuatan Nomor Surat dan antrean pencetakan dokumen fisik PAK Konversi.",
              icon: Printer,
              state: (s === 'disetujui' && !hasNomorSurat) ? 'active' : ((s === 'disetujui' && hasNomorSurat) ? 'done' : 'wait')
          },
          {
              id: 4,
              title: "Tanda Tangan Pejabat Penilai",
              desc: "Proses tanda tangan basah berkas fisik oleh Pejabat Penilai Kinerja.",
              icon: PenTool,
              state: (s === 'disetujui' && hasNomorSurat) ? 'active' : 'wait'
          }
      ];

      return (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8 animate-fade-in-up">
              <h3 className="text-lg font-bold text-slate-800 mb-8 border-b pb-4 flex items-center justify-between">
                 Alur Proses Usulan Anda
                 {updatedAt && (
                   <span className="text-[11px] text-slate-400 font-medium flex items-center gap-1.5 hidden md:flex">
                         <CalendarClock size={14} /> Update Terakhir: {formatDateTime(updatedAt)}
                     </span>
                 )}
              </h3>
              
              <div className="relative pl-2">
                 <div className="absolute left-[1.65rem] top-4 bottom-8 w-0.5 bg-slate-200 rounded"></div>

                 <div className="space-y-8 relative">
                    {steps.map((step, index) => {
                        const Icon = step.icon;
                        let colorClasses = "";
                        let iconContent = <Icon size={16} strokeWidth={2.5} />;
                        
                        if (step.state === 'done') {
                            colorClasses = "bg-emerald-500 text-white border-white ring-4 ring-emerald-50";
                            iconContent = <Check size={18} strokeWidth={3} />;
                        } else if (step.state === 'active') {
                            colorClasses = "bg-blue-600 text-white border-white ring-4 ring-blue-50 shadow-lg shadow-blue-600/30";
                        } else if (step.state === 'error') {
                            colorClasses = "bg-rose-500 text-white border-white ring-4 ring-rose-50 shadow-lg shadow-rose-600/30";
                            iconContent = <X size={18} strokeWidth={3} />;
                        } else {
                            colorClasses = "bg-slate-100 text-slate-400 border-white ring-4 ring-white";
                        }

                        return (
                            <div key={step.id} className="relative flex items-start gap-5">
                                <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center shrink-0 relative z-10 transition-all duration-500 ease-out ${colorClasses}`}>
                                   {iconContent}
                                </div>
                             
                                <div className="flex-1 pt-1.5 pb-2">
                                    <h4 className={`font-bold text-base md:text-lg tracking-tight ${step.state === 'active' ? 'text-blue-800' : step.state === 'error' ? 'text-rose-800' : step.state === 'done' ? 'text-slate-800' : 'text-slate-400'}`}>
                                       {step.title}
                                    </h4>
                                    <p className={`text-sm mt-1 leading-relaxed max-w-2xl ${step.state === 'error' ? 'text-rose-600 font-medium' : step.state === 'active' ? 'text-slate-600 font-medium' : 'text-slate-500'}`}>
                                        {step.desc}
                                    </p>
                                    
                                    {step.state === 'active' && step.id === 1 && (
                                        <button onClick={() => onSwitchTab('profil')} className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors inline-flex items-center gap-2">
                                            Lengkapi & Ajukan Sekarang <ArrowRight size={14} />
                                        </button>
                                    )}
                                    
                                    {step.state === 'error' && (
                                        <button onClick={() => onSwitchTab('inputpak')} className="mt-3 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-lg transition-colors inline-flex items-center gap-2 shadow-lg shadow-rose-600/20">
                                            Perbaiki Data Saya <ArrowRight size={14} />
                                        </button>
                                    )}
                                    
                                    {s === 'disetujui' && step.state === 'active' && (step.id === 3 || step.id === 4) && (
                                        <button onClick={handleOpenPreview} className="mt-3 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors inline-flex items-center gap-2 shadow-lg shadow-emerald-600/20">
                                            <Download size={14} /> Lihat Draft Berkas PAK Sementara
                                        </button>
                                    )}
                                 </div>
                            </div>
                        )
                    })}
                 </div>
              </div>
              
              {updatedAt && (
                  <div className="mt-6 md:hidden text-center text-xs text-slate-400 font-medium border-t border-slate-100 pt-4">
                    Update Terakhir: {formatDateTime(updatedAt)}
                  </div>
              )}
          </div>
      );
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto animate-fade-in-up pb-24 relative">
      <div className="mb-8">
        <h1 className="text-2xl md:text-4xl font-serif text-slate-900 mb-2">{getGreeting()}, {user.name.split(' ')[0]}.</h1>
       <p className="text-slate-500 text-sm md:text-base">
        {user.role === 'admin' 
          ? 'Selamat datang di panel kontrol Administrator.' 
          : user.role === 'satlak'
          ? 'Berikut adalah pantauan real-time usulan PAK dari guru-guru di wilayah Anda.'
          : 'Pantau progres pengajuan Penilaian Angka Kredit Anda di sini.'}
      </p>
      </div>

      {(user.role !== 'guru' || kpAllowed) && (
        <div className="mb-7 bg-blue-50 border border-blue-200 rounded-2xl p-5 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div><h2 className="text-lg font-bold text-slate-900">Perlu bantuan mengusulkan Kenaikan Pangkat?</h2><p className="text-base text-slate-600 mt-1">Lihat panduan 6 langkah dengan tulisan besar dan bahasa sederhana.</p></div>
          <button onClick={() => window.location.assign('/panduan-kenaikan-pangkat')} className="bg-blue-700 text-white rounded-xl px-5 py-3 font-bold whitespace-nowrap">Lihat Panduan KP →</button>
        </div>
      )}
      {(user.role === 'satlak' || user.role === 'admin') && (
         <div className="mt-8">
          {user.role === 'satlak' && deadlineInfo.date && (
            <div className={`mb-6 p-4 rounded-xl border flex items-start gap-3 shadow-sm ${deadlineInfo.isClosed ? 'bg-red-50 border-red-200 text-red-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
              <AlertTriangle className={`shrink-0 ${deadlineInfo.isClosed ? 'text-red-600' : 'text-blue-600'}`} size={24} />
              <div>
                <h3 className="font-bold">{deadlineInfo.isClosed ? 'Jadwal Verifikasi Telah Ditutup' : 'Batas Waktu Verifikasi'}</h3>
                <p className="text-sm mt-1">
                  {deadlineInfo.isClosed 
                    ? `Akses untuk melakukan persetujuan atau penolakan PAK telah dikunci sejak ${new Date(deadlineInfo.date).toLocaleDateString('id-ID', { dateStyle: 'long' })}. Anda hanya dapat melihat riwayat data.`
                    : `Batas akhir verifikasi Satlak adalah tanggal ${new Date(deadlineInfo.date).toLocaleDateString('id-ID', { dateStyle: 'long' })}.`}
                </p>
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-4 gap-2">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Usulan Masuk Terbaru</h2>
              <p className="text-sm text-slate-500">Menampilkan daftar pengajuan teratas berdasarkan waktu submit.</p>
            </div>
            <button 
              onClick={() => onSwitchTab('verifikasi-pak')} 
              className="text-sm font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 bg-blue-50 px-3 py-1.5 rounded-lg transition-colors"
            >
              Verifikasi Lengkap <ArrowRight size={16} />
            </button>
          </div>

          {loadingList ? (
            <div className="bg-white p-10 rounded-2xl border border-slate-200 flex flex-col items-center justify-center text-slate-400 gap-3 shadow-sm">
              <Loader2 className="animate-spin text-blue-500" size={32} />
              <p className="text-sm font-medium">Memuat data usulan...</p>
            </div>
          ) : recentList.length === 0 ? (
            <div className="bg-white p-10 rounded-2xl border border-slate-200 flex flex-col items-center justify-center text-slate-400 gap-3 shadow-sm">
              <Activity size={40} className="text-slate-300" />
              <p className="text-slate-500 font-medium">Belum ada usulan masuk di wilayah ini.</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="divide-y divide-slate-100">
                {recentList.map((item) => (
                  <div key={item.pak_id} className="p-4 sm:p-5 hover:bg-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-colors group">
                    
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center font-bold text-slate-600 text-sm shrink-0">
                        {item.full_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-800 text-sm sm:text-base leading-tight">
                          {item.full_name}
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {item.nip} <span className="hidden sm:inline">•</span> <span className="block sm:inline">{item.unit_kerja || '-'}</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-6 sm:w-auto w-full border-t sm:border-t-0 border-slate-100 pt-3 sm:pt-0">
                      <div className="text-left sm:text-right flex flex-col sm:block">
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold mb-1 sm:hidden">Waktu & Status</span>
                        <p className="text-xs font-medium text-slate-600 sm:mb-1">
                          {new Date(item.updated_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                        <StatusBadge status={item.status} />
                      </div>
                      <button 
                        onClick={() => onSwitchTab('verifikasi-pak')} 
                        title="Tindak Lanjuti di Menu Verifikasi"
                        className="w-10 h-10 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all shadow-sm shrink-0"
                      >
                        <ArrowRight size={18} />
                      </button>
                     </div>

                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {user.role === 'guru' && (
        <div className="mt-2">
          {renderGuruTimeline()}
        </div>
      )}

      {/* MODAL PREVIEW PDF */}
      {showPreviewModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/90 backdrop-blur-sm px-2 md:px-10 py-6">
          <div className="bg-white w-full max-w-6xl h-full flex flex-col rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up relative z-[10000]">
            
            <div className="flex justify-between items-center p-4 border-b bg-slate-50 z-20 shadow-sm shrink-0">
              <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm md:text-base">
                <Eye size={18} className="text-blue-600"/> Dokumen PAK Sementara (Belum TTD)
              </h3>
              <div className="flex items-center gap-2 md:gap-3">
                <button 
                  onClick={handleDownloadPDF} 
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs md:text-sm font-semibold rounded-lg transition-colors shadow-sm"
                >
                  <Download size={16} /> <span className="hidden md:inline">Download PDF</span>
                </button>
                <button 
                  onClick={() => setShowPreviewModal(false)} 
                  className="p-2 hover:bg-rose-100 text-slate-500 hover:text-rose-600 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* PREVIEW KERTAS (Padding-Y ditambah ke py-10) */}
            <div className="flex-1 w-full bg-slate-400 overflow-auto relative custom-scrollbar z-10 p-4 md:p-8">
              <div className="flex justify-center min-w-max py-10">
                <div ref={printRef} className="shrink-0 drop-shadow-2xl">
                   <TabelPreviewPAK 
                      calculation={previewCalculation} 
                      selectedItem={{
                         ...profileData, 
                         ak_lama: Number(String(pakData.status?.ak_lama || '0').replace(',', '.')),
                         skp_data: typeof pakData.status?.skp_data === 'string' ? JSON.parse(pakData.status.skp_data) : (pakData.status?.skp_data || [])
                      }} 
                      nomorSurat={pakData.status?.nomor_surat || '...../KG.04.04'} 
                      isDraft={true} 
                      forPrint={false} 
                      tanggalTtd={pakData.status?.tgl_cetak || ''} 
                   />
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;